import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Box, ShoppingCart, Clock } from "lucide-react";
import PageSEO from "../components/SEO";
import useContent from "../lib/content";

/**
 * Phase 46b — Home is a two-tile services landing.
 *
 * Tile 1 (ACTIVE): "Enviar paquete" — the customer ships their own
 * goods to the camps. Copy communicates the 35 € / 10 kg included
 * baseline + 2,90 €/kg beyond that.
 *
 * Tile 2 (COMING SOON): "Enviar tu compra" — grocery bundling from
 * the Mercadona catalog. Non-clickable placeholder while the shop is
 * finalised; badge advertises the 2,00 €/kg storefront rate and a
 * release date is displayed instead of a CTA.
 *
 * The mint-green backdrop is inherited from ``AppShell`` / ``GlobalShell``.
 */
export default function Home() {
  const { fields } = useContent("home");
  return (
    <div
      data-testid="home-page"
      className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-16"
    >
      <PageSEO
        title={fields.seo_title || "MercaGo · Envío a los campamentos saharauis"}
        description={fields.seo_description}
        canonical="https://mercago.one/envio/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://mercago.one/#organization",
              "name": "MercaGo",
              "url": "https://mercago.one/",
              "logo": "https://mercago.one/logo.png",
              "description": "Envío de productos halal desde España a los campamentos saharauis.",
              "areaServed": [
                { "@type": "Country", "name": "España" },
                { "@type": "Country", "name": "Argelia" },
              ],
              "email": "info@mercago.one",
            },
            {
              "@type": "WebSite",
              "@id": "https://mercago.one/#website",
              "url": "https://mercago.one/",
              "name": "MercaGo",
              "inLanguage": "es-ES",
              "publisher": { "@id": "https://mercago.one/#organization" },
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://mercago.one/envio/buscar?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }}
      />

      {/* Fase 4 · CMS — hero opcional. Solo se renderiza cuando el admin ha
          configurado título o imagen. Se coloca ARRIBA de los tiles servicios
          para no romper el layout existente cuando no hay contenido. */}
      {(fields.hero_title || fields.hero_subtitle || fields.hero_image_url) && (
        <section
          data-testid="home-hero"
          className="mb-8 sm:mb-12 grid md:grid-cols-2 gap-6 items-center"
        >
          <div>
            {fields.hero_title && (
              <h1
                data-testid="home-hero-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-neutral-900"
              >
                {fields.hero_title}
              </h1>
            )}
            {fields.hero_subtitle && (
              <p
                data-testid="home-hero-subtitle"
                className="mt-4 text-base sm:text-lg text-neutral-600 whitespace-pre-line"
              >
                {fields.hero_subtitle}
              </p>
            )}
            {fields.hero_cta_label && (
              <Link
                to="/enviar-paquete"
                data-testid="home-hero-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white px-6 py-3 font-semibold text-sm"
              >
                {fields.hero_cta_label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
          {fields.hero_image_url && (
            <img
              src={
                fields.hero_image_url.startsWith("http")
                  ? fields.hero_image_url
                  : `${window.location.origin}${fields.hero_image_url}`
              }
              alt=""
              data-testid="home-hero-image"
              className="w-full rounded-3xl object-cover max-h-[420px]"
              loading="eager"
            />
          )}
        </section>
      )}

      <section
        data-testid="home-hero-tiles"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8"
      >
        {/* Tile 1 — ACTIVE, ambar accent */}
        <Link
          to="/enviar-paquete"
          data-testid="hero-tile-enviar-paquete"
          className="group block rounded-3xl bg-amber-50 border-2 border-amber-200 p-6 sm:p-10 min-h-[280px] sm:min-h-[360px] hover:shadow-xl hover:border-amber-400 hover:-translate-y-1 transition-all"
        >
          <div className="flex flex-col h-full">
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-3xl bg-amber-100 grid place-items-center text-amber-700 shrink-0 mb-5">
              <Box className="h-10 w-10 sm:h-12 sm:w-12" strokeWidth={1.75} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-neutral-900">
              Enviar paquete
            </h2>
            <p
              data-testid="hero-tile-enviar-paquete-copy"
              className="mt-2 text-sm sm:text-base text-neutral-600 max-w-md"
            >
              Envía paquetes a los campamentos saharauis. Envío mínimo{" "}
              <strong>35 €</strong> con <strong>10 kg incluidos</strong>. A
              partir del kg 11, <strong>2,90 € por kg</strong>.
            </p>
            <div
              data-testid="hero-tile-enviar-paquete-cta"
              className="mt-auto pt-5 flex items-center gap-2 text-amber-800 font-bold text-sm group-hover:translate-x-1 transition-transform"
            >
              Empezar <ArrowRight className="h-4 w-4" />
            </div>
          </div>
        </Link>

        {/* Tile 2 — COMING SOON, no navigation. Emerald accent, badge, date placeholder. */}
        <div
          data-testid="hero-tile-enviar-compra"
          aria-disabled="true"
          className="group block rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-6 sm:p-10 min-h-[280px] sm:min-h-[360px] cursor-default"
        >
          <div className="flex flex-col h-full">
            <div className="flex items-start justify-between">
              <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-3xl bg-emerald-600 grid place-items-center text-white shrink-0 mb-5">
                <ShoppingCart className="h-10 w-10 sm:h-12 sm:w-12" strokeWidth={1.75} />
              </div>
              <span
                data-testid="hero-tile-enviar-compra-price"
                className="inline-flex items-center rounded-full bg-emerald-600 text-white text-sm font-bold px-3 py-1"
              >
                2,00 €/kg
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-neutral-900">
              Enviar tu compra
            </h2>
            <p className="mt-2 text-sm sm:text-base text-neutral-600 max-w-md">
              Elige productos del catálogo y envíaselos directamente a tu
              familia. Más de 2 800 productos seleccionados.
            </p>
            <div
              data-testid="hero-tile-enviar-compra-coming-soon"
              className="mt-auto pt-5 flex items-center gap-2 text-slate-500 font-semibold text-sm"
            >
              <Clock className="h-4 w-4" />
              Próximamente · El 15/09/2026
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
